import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Send } from 'lucide-react';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import ChaseHi from '../../assets/images/ChaseHi.png';
import { askChase, loadThread } from '../../utils/chaseService';
import { palette } from '../pricing/pricingTokens';
import haptics from '../../utils/haptics';

/**
 * The conversation with Chase about one card.
 *
 * The thread belongs to the card, so closing this and reopening it on the same
 * card continues where it stopped, and walking to another card starts clean.
 * That is handled by the key in `chaseService`, not here; this component just
 * loads whatever thread the card has.
 *
 * A door tapped on the way in is sent as a question the moment the history has
 * loaded, so the user sees their own message before his answer, exactly as if
 * they had typed it.
 */

const QUESTION = {
  pricing: 'Dive into pricing',
  facts: 'Interesting facts',
};

/** Reopening an existing thread asks nothing; it just shows the conversation. */
const RESUME = 'resume';

export default function ChaseChat({
  open, card, door, selectedTier, onClose, theme, isDark = true,
}) {
  const c = palette(theme, isDark);
  const s = styles(c);

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  // The door that opened this session, once. Without the ref, any re-render
  // while the request is in flight fires the same question a second time.
  const firedRef = useRef(null);

  const scrollDown = useCallback(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, []);

  const send = useCallback(async (text, doorKey) => {
    if (!text.trim() || thinking) return;
    setError(null);
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', content: text }]);
    setThinking(true);
    try {
      const { answer, sources } = await askChase({
        card, door: doorKey || 'custom', message: doorKey ? '' : text, selectedTier,
      });
      setMessages((prev) => [...prev,
        { id: `chase-${Date.now()}`, role: 'chase', content: answer, sources }]);
    } catch (err) {
      setError(err.message || 'Chase could not answer just now.');
    } finally {
      setThinking(false);
    }
  }, [card, selectedTier, thinking]);

  // Load the thread, then fire the door if one brought us here.
  useEffect(() => {
    if (!open || !card?.name) return undefined;
    let cancelled = false;

    setLoading(true);
    loadThread(card).then((history) => {
      if (cancelled) return;
      setMessages(history);
      setLoading(false);
      if (door && door !== RESUME && firedRef.current !== door) {
        firedRef.current = door;
        if (QUESTION[door]) send(QUESTION[door], door);
        else setTimeout(() => inputRef.current?.focus(), 260);
      }
    });

    return () => { cancelled = true; };
    // `send` is intentionally out: it changes identity with `thinking`, and
    // re-running this effect mid-answer would reload the thread underneath it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card?.name, card?.set_number, door]);

  // A closed sheet forgets which door opened it, so the next open fires again.
  useEffect(() => { if (!open) firedRef.current = null; }, [open]);

  useEffect(() => { scrollDown(); }, [messages, thinking, scrollDown]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setDraft('');
    send(text);
  };

  return (
    <div style={s.sheet} role="dialog" aria-label={`Chat with Chase about ${card?.name || 'this card'}`}>
      <div style={s.bar}>
        <img src={ChaseHi} alt="" style={s.art} />
        <div>
          <div style={s.who}>Chase</div>
          <div style={s.about}>
            {card?.name}
            {card?.set_name ? ` · ${card.set_name}` : ''}
            {card?.set_number ? ` #${card.set_number}` : ''}
          </div>
        </div>
        <button
          type="button"
          style={s.close}
          onClick={() => { haptics.tap(); onClose(); }}
          aria-label="Close chat"
        >
          <X size={15} />
        </button>
      </div>

      <div
        style={s.messages}
        ref={scrollRef}
        onClick={(e) => {
          // A plain anchor inside a Capacitor webview navigates the app away
          // from itself and there is no way back. Every link Chase writes opens
          // in the system browser instead.
          const link = e.target.closest?.('a[data-chase-link]');
          if (!link) return;
          e.preventDefault();
          openExternal(link.getAttribute('href'));
        }}
      >
        {loading && <div style={s.quiet}>Opening...</div>}

        {!loading && !messages.length && !thinking && (
          <div style={s.quiet}>Ask Chase anything about this card.</div>
        )}

        {/* Chase is told to use <b> and nothing else, and his text never leaves
            our own edge function, so the tag set is ours to allow rather than
            the model's to widen. Anything the user typed is rendered as text. */}
        {messages.map((m) => (m.role === 'chase' ? (
          <div key={m.id} style={s.hisWrap}>
            <div
              className="cc-chase-msg"
              style={{ ...s.msg, ...s.his }}
              dangerouslySetInnerHTML={{ __html: safe(m.content) }}
            />
            {/* Where he went to find it. An answer that read something should
                say what it read, and the chips are the shortest way to say it. */}
            {Array.isArray(m.sources) && m.sources.length > 0 && (
              <div style={s.sources}>
                {m.sources.map((src) => (
                  <button
                    key={src.uri}
                    type="button"
                    style={s.source}
                    onClick={() => openExternal(src.uri)}
                  >
                    {src.title}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div key={m.id} style={{ ...s.msg, ...s.mine }}>{m.content}</div>
        )))}

        {thinking && (
          <div style={{ ...s.msg, ...s.his, ...s.typing }}>
            <i style={{ ...s.dot, animationDelay: '0ms' }} />
            <i style={{ ...s.dot, animationDelay: '180ms' }} />
            <i style={{ ...s.dot, animationDelay: '360ms' }} />
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}
      </div>

      <form style={s.composer} onSubmit={submit}>
        <input
          ref={inputRef}
          style={s.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask about this card"
          aria-label="Your question"
        />
        <button type="submit" style={s.send} disabled={!draft.trim() || thinking} aria-label="Send">
          <Send size={16} />
        </button>
      </form>
    </div>
  );
}

/**
 * A plain anchor inside a Capacitor webview navigates the app away from itself
 * and there is no way back, so everything outbound goes to the system browser.
 */
function openExternal(url) {
  if (!url) return;
  if (Capacitor.isNativePlatform()) Browser.open({ url }).catch(() => {});
  else window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Bold and links are the only markup Chase is allowed, so they are the only
 * markup that survives. Everything is escaped first and then exactly those two
 * are let back in, which means a stray angle bracket in a card name cannot
 * become an element and a link cannot carry an event handler.
 *
 * Hrefs are restricted to the marketplaces we hand him. He is told to use only
 * the URLs he was given; this is what makes that true rather than hoped for,
 * and it also closes off javascript: and data: entirely.
 */
const LINK_HOSTS = /^https:\/\/(www\.)?(ebay\.com|tcgplayer\.com)\//i;

function safe(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/&lt;b&gt;/g, '<b>')
    .replace(/&lt;\/b&gt;/g, '</b>')
    .replace(/&lt;a href=&quot;([^&]*?)&quot;&gt;([\s\S]*?)&lt;\/a&gt;/g, (whole, href, label) => {
      const url = String(href).replace(/&amp;/g, '&');
      if (!LINK_HOSTS.test(url)) return label;
      return `<a href="${url.replace(/"/g, '&quot;')}" data-chase-link="1">${label}</a>`;
    })
    .replace(/\n/g, '<br />');
}

const styles = (c) => ({
  sheet: {
    position: 'fixed', inset: 0, zIndex: 900, background: c.sheet,
    display: 'flex', flexDirection: 'column',
    paddingTop: 'var(--safe-area-top, 0px)', paddingBottom: 'var(--safe-area-bottom, 0px)',
    animation: 'cc-rise 0.26s cubic-bezier(0.2, 0.8, 0.3, 1) both',
  },
  bar: {
    display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 14px 12px',
    borderBottom: `0.5px solid ${c.divider}`, flex: 'none',
  },
  art: { height: '34px', width: 'auto', display: 'block' },
  who: { fontSize: '14.5px', fontWeight: '600', color: c.white },
  about: { fontSize: '11.5px', color: c.muted, marginTop: '1px' },
  close: {
    marginLeft: 'auto', border: 0, background: c.lifted, color: c.secondary,
    width: '32px', height: '32px', borderRadius: '50%', display: 'inline-flex',
    alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
  },

  messages: {
    flex: 1, overflowY: 'auto', padding: '16px 14px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  quiet: { alignSelf: 'center', fontSize: '12.5px', color: c.faint, padding: '18px 0' },

  msg: {
    maxWidth: '84%', fontSize: '14px', lineHeight: 1.55, padding: '11px 13px',
    borderRadius: '15px', wordBreak: 'break-word',
  },
  hisWrap: { alignSelf: 'flex-start', maxWidth: '88%', display: 'flex', flexDirection: 'column', gap: '6px' },
  his: { background: c.lifted, color: c.primary, borderBottomLeftRadius: '5px', maxWidth: '100%' },
  sources: { display: 'flex', flexWrap: 'wrap', gap: '6px', paddingLeft: '2px' },
  source: {
    border: `0.5px solid ${c.chipBorder}`, background: 'transparent', color: c.secondary,
    fontFamily: 'inherit', fontSize: '10.5px', padding: '4px 9px', borderRadius: '999px',
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
  mine: { alignSelf: 'flex-end', background: c.blue, color: '#04121c', fontWeight: '500', borderBottomRightRadius: '5px' },

  typing: { display: 'inline-flex', gap: '4px', alignItems: 'center', padding: '14px 15px' },
  dot: {
    width: '5px', height: '5px', borderRadius: '50%', background: c.secondary,
    display: 'block', animation: 'cc-pulse 1.3s ease-in-out infinite',
  },
  error: {
    alignSelf: 'center', fontSize: '12.5px', color: c.red, background: c.redTint,
    border: `0.5px solid ${c.red}`, borderRadius: '10px', padding: '9px 12px', textAlign: 'center',
  },

  composer: {
    flex: 'none', display: 'flex', gap: '8px', padding: '11px 14px 14px',
    borderTop: `0.5px solid ${c.divider}`,
  },
  input: {
    flex: 1, background: c.input, border: `0.5px solid ${c.chipBorder}`,
    borderRadius: '999px', padding: '11px 15px', fontSize: '14px', color: c.white,
    fontFamily: 'inherit', outline: 'none', minWidth: 0,
  },
  send: {
    width: '38px', height: '38px', flex: 'none', borderRadius: '50%', border: 0,
    background: c.blue, color: '#04121c', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  },
});
